<template>
    <div class="space-y-4">
        <div class="space-y-3">
            <label>Patient Information</label>
            <p>Who is this appointment for?</p>
            <p v-if="error" class="text-xs text-red-600">Please choose</p>
        </div>
        <div 
            @click="selected = 'me'" 
            class="relative px-6 py-4 bg-white rounded-2xl"
            :class="selected === 'me' ? 'outline outline-[3px] outline-primary-500 ' : ''"
        >
            <div v-if="selected === 'me'" class="absolute w-5 h-5 bg-white rounded-full -top-2 -right-2">
                <CheckCircleIcon class="w-5 h-5 text-primary-500"/>
            </div>
            <p>For Me</p>

        </div>
        <div 
            @click="selected = 'dependent'" 
            class="relative px-6 py-4 bg-white rounded-2xl"
            :class="selected === 'dependent' ? 'outline outline-[3px] outline-primary-500 ' : ''"
        >
            <div v-if="selected === 'dependent'" class="absolute w-5 h-5 bg-white rounded-full -top-2 -right-2">
                <CheckCircleIcon class="w-5 h-5 text-primary-500"/>
            </div>
            <div class="" :class="selected === 'dependent' ? 'pb-2.5 border-b border-gray-50' : ''">

                <p>For my dependent</p>
            </div>
            <div v-if="selected === 'dependent'" class="py-2.5 space-y-2.5">

                <div class="grid grid-cols-12 col-span-12 gap-6">
                    <div class="col-span-6">
                        <TextInput 
                            v-model="dependentForm.first_name"
                            id="first_name"
                            name="first_name"
                            label="First Name"
                            placeholder="First Name"
                        />
                    </div>
                    <div class="col-span-6">
                        <TextInput 
                            v-model="dependentForm.middle_name"
                            id="middle_name"
                            name="middle_name"
                            label="Middle Name"
                            placeholder="Middle Name"
                        />
                    </div>

                    <div class="col-span-6">
                        <TextInput
                            v-model="dependentForm.last_name"
                            id="last_name"
                            name="last_name"
                            label="Last Name"
                            placeholder="Last Name"
                        />
                    </div>

                    <div class="col-span-6">
                        <MultiselectInput
                            v-model="dependentForm.sex"
                            id="sex"
                            name="sex"
                            label=""
                            placeholder="Sex"
                            :options="sexes"
                        />
                    </div>

                    <div class="col-span-6">
                        <DatePicker
                            v-model="dependentForm.birthdate"
                            id="birthdate"
                            name="birthdate"
                            label=""
                            placeholder="Birthday"
                            isTodayMaxDate
                        />
                    </div>

                    <div class="col-span-6">
                        <mobile-number 
                            v-model="dependentForm.cellphone_number"
                            id="cellphone_number"
                            name="cellphone_number"
                            label="Mobile Number"
                            placeholder="Mobile Number"
                        />
                    </div>
                    <div class="col-span-6">
                        <TextInput 
                            v-model="dependentForm.telephone_number"
                            id="telephone_number"
                            name="telephone_number"
                            label="Telephone Number"
                            placeholder="Telephone Number"
                        />
                    </div>

                    <div class="col-span-6">
                        <TextInput
                            v-model="dependentForm.email_address"
                            id="email_address"
                            name="email_address"
                            label="Email Address"
                            placeholder="Email Address"
                        />
                    </div>
                </div>

                <p>Please answer the following questions:</p>
                <div>
                    <p>What is your relationship with this dependent?</p>
                    <div class="grid grid-cols-2 gap-4 mt-2.5">
                        <radio-group
                        label-class="text-sm"
                        :options="options"
                        v-model="dependentForm.dependent_relationship"
                        name="options"
                        />
                    </div>
                </div>
                <div class="border-b border-gray-50 pb-2.5">
                    <p>Is this dependent a minor?</p>
                    <div class="grid grid-cols-2 gap-4 mt-2.5">
                        <radio-group
                        label-class="text-sm"
                        :options="minor_options"
                        v-model="dependentForm.is_minor"
                        name="minor-options"
                        />
                    </div>
                </div>
                <div>
                    <p class="text-sm text-darkgray-400">Note: Additional medical information about your dependent will be requested after you submit this booking request.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch} from 'vue';
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import RadioGroup from '@/components/inputs/RadioGroup.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import MobileNumber from "@/components/inputs/MobileNumber.vue";
import MultiselectInput from "@/components/inputs/MultiselectInput.vue";
import { useBookingStore } from '@/store/booking';

const props = defineProps({
    patient: {
        type: Object
    },
    error: {
        type: Boolean,
    }
})

const emit = defineEmits(['selected-patient']);

const bookingStoreState = useBookingStore();


const selected = ref(null);
const dependentForm = ref(bookingStoreState.bookingPayload.dependent_patient);

const options = [
    {
        id: 1,
        value: 'child',
        label: 'My child',
    },
    {
        id: 2,
        value: 'spouse',
        label: 'My spouse',
    },
    {
        id: 3,
        value: 'parent',
        label: 'My parent',
    },
    {
        id: 4,
        value: 'others',
        label: 'Others',
    }
]
const minor_options = [
    {
        id: 1,
        value: true,
        label: 'Yes',
    },
    {
        id: 2,
        value: false,
        label: 'No',
    },
]

const sexes = [
    { id: '1', label: 'Male', value: 'Male' },
    { id: '2', label: 'Female', value: 'Female' },
    // { id: '3', label: 'Prefer not to Say', value: 'Prefer not to Say' },
];

const checkOption = () => {
    if(selected.value === 'dependent'){
        emit('selected-patient', {
            selected: selected.value,
            dependentForm: dependentForm.value
        });
    }
    else{
        emit('selected-patient',{
            selected: selected.value,
            dependentForm: null
        })
    }
}

watch([selected, dependentForm], () => {
    checkOption();
}, { deep: true });

</script>